import { fetchWrapper } from '@/reducers/fetch'
import type {
  YearEndModuleStatusOptions,
  YearEndModuleSubstepId,
} from '@/features/year-end-module-status/yearEndModuleStatus.slice'
import { apiAxios } from '@/api'

export enum TaxfyleQuestionType {
  bool = 'BOOL',
  numeric = 'NUMERIC',
  text = 'TEXT',
  dateTime = 'DATE_TIME',
  singleSelect = 'SINGLE_SELECT',
  multiSelect = 'MULTI_SELECT',
}

export type TaxfyleAnswer = string | number | boolean | string[] | null

export interface TaxfyleQuestionOption {
  id: string
  text: string
}

export interface TaxfyleShortFormQuestion {
  id: string
  type: TaxfyleQuestionType
  question: string
  description: string
  answer: TaxfyleAnswer
  options?: TaxfyleQuestionOption[]
  readOnly: boolean
}

export interface TaxfyleShortFormQuestionnaire {
  jobId: string
  formType: string
  taxYear: string
  outstandingFormCount: number
  booksLocked: boolean
  readyToSubmit: boolean
  questions: TaxfyleShortFormQuestion[]
}

export enum TaxfyleShortFormSubmitOutcome {
  submitted = 'submitted',
  needsAnswers = 'needs_answers',
}

export type TaxfyleShortFormSubmitResult =
  | {
      outcome: TaxfyleShortFormSubmitOutcome.submitted
      jobId: string
      status: string
      released: boolean
      nextFormType: string | null
    }
  | {
      outcome: TaxfyleShortFormSubmitOutcome.needsAnswers
      questionnaire: TaxfyleShortFormQuestionnaire
    }

const BASE_PATH = '/finances/api/v1/taxfyle_short_form'

export enum PostTaxYearState {
  questionnaire = 'questionnaire',
  awaitingHandoff = 'awaiting_handoff',
  withTaxPreparer = 'with_tax_preparer',
  notApplicable = 'not_applicable',
}

export interface PostTaxYearForm {
  filingFormId: number
  formType: string
  answered: boolean
  jobStatus: string | null
  jobStep: string | null
  withPreparer: boolean
  moduleStatus: YearEndModuleStatusOptions | null
  substepIdentifier: YearEndModuleSubstepId | null
}

export interface PostTaxYearStatus {
  taxYear: string
  state: PostTaxYearState
  booksLocked: boolean
  canEdit: boolean
  outstandingFormCount: number
  forms: PostTaxYearForm[]
}

export const FETCH_POST_TAX_YEAR_STATUS_KEY = (taxYear: string) =>
  `FETCH_POST_TAX_YEAR_STATUS_KEY_${taxYear}`

export const fetchPostTaxYearStatus = (taxYear: string) =>
  fetchWrapper({
    fetchKey: FETCH_POST_TAX_YEAR_STATUS_KEY(taxYear),
    fetchFunction: async () => {
      const json = await apiAxios.get<PostTaxYearStatus>(
        `${BASE_PATH}/status`,
        {
          params: { taxYear },
        }
      )
      return json.data
    },
  })

export const FETCH_TAXFYLE_SHORT_FORM_KEY = 'FETCH_TAXFYLE_SHORT_FORM_KEY'
export const SAVE_TAXFYLE_SHORT_FORM_KEY = 'SAVE_TAXFYLE_SHORT_FORM_KEY'
export const SUBMIT_TAXFYLE_SHORT_FORM_KEY = 'SUBMIT_TAXFYLE_SHORT_FORM_KEY'

export const fetchTaxfyleShortFormQuestionnaire = (taxYear: string) =>
  fetchWrapper({
    fetchKey: FETCH_TAXFYLE_SHORT_FORM_KEY,
    fetchFunction: async () => {
      const json = await apiAxios.get<TaxfyleShortFormQuestionnaire>(
        `${BASE_PATH}/questionnaire`,
        { params: { taxYear } }
      )
      return json.data
    },
  })

export const saveTaxfyleShortFormAnswers = (
  taxYear: string,
  answers: Record<string, Exclude<TaxfyleAnswer, null>>
) =>
  fetchWrapper({
    fetchKey: SAVE_TAXFYLE_SHORT_FORM_KEY,
    fetchFunction: async () => {
      const json = await apiAxios.put<TaxfyleShortFormQuestionnaire>(
        `${BASE_PATH}/answers`,
        { taxYear, answers }
      )
      return json.data
    },
  })

export const submitTaxfyleShortForm = (taxYear: string) =>
  fetchWrapper({
    fetchKey: SUBMIT_TAXFYLE_SHORT_FORM_KEY,
    fetchFunction: async () => {
      const json = await apiAxios.post<TaxfyleShortFormSubmitResult>(
        `${BASE_PATH}/submit`,
        { taxYear }
      )
      return json.data
    },
  })
